import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "./AuthContext";
import { getAllAccountsInRealTime } from "../Config/db/account.operations";

export const AccountContext = createContext();

/**
 * Account model:
 *  - ONE primary account (accountRole === "primary") — the real community bank balance.
 *  - N virtual accounts (everything else) — categorise where money comes from / goes to.
 *  - Every virtual-account transaction creates a matching entry on the primary account.
 */
export const AccountContextProvider = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const { userInfo } = useContext(AuthContext);

  useEffect(() => {
    if (userInfo?.communityCode) {
      const unsub = getAllAccountsInRealTime(userInfo.communityCode, (accountsInfo) => {
        setAccounts(accountsInfo);
      });
      return () => unsub?.();
    }
  }, [userInfo?.communityCode]);

  const primaryAccount = useMemo(
    () => accounts.find((a) => a.accountRole === "primary") || null,
    [accounts]
  );

  const cashAccount = useMemo(
    () => accounts.find((a) => a.accountRole === "cash") || null,
    [accounts]
  );

  const virtualAccounts = useMemo(
    () => accounts.filter((a) => a.accountRole !== "primary" && a.accountRole !== "cash"),
    [accounts]
  );

  const contextValue = useMemo(
    () => ({ accounts, primaryAccount, cashAccount, virtualAccounts }),
    [accounts, primaryAccount, cashAccount, virtualAccounts]
  );

  return (
    <AccountContext.Provider value={contextValue}>
      {children}
    </AccountContext.Provider>
  );
};
